import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { useSession } from "../auth/session";
import { handleCallback } from "../auth/oidc";

// OIDC redirect target (E1 US-1.1). Keycloak sends the browser back here with a
// one-time authorization code; this exchanges it (PKCE) for tokens, establishes the
// session, and forwards to where the user was headed. On any failure — bad state,
// denied consent, expired code — it renders an error with a way back to /login and
// establishes NO session. Public route: it must run before RequireAuth would bounce
// the code-bearing URL to /login.
type Status = "exchanging" | "error";

export function AuthCallbackPage() {
  const { t } = useTranslation();
  const { setSession } = useSession();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("exchanging");
  // The authorization code is single-use — guard against React's double effect
  // invocation (StrictMode) firing a second, now-invalid token exchange.
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;

    void (async () => {
      try {
        const { session, returnTo } = await handleCallback(window.location.search);
        setSession(session);
        // replace: drop the code-bearing URL from history so Back doesn't re-trigger it.
        void navigate(returnTo, { replace: true });
      } catch {
        setStatus("error");
      }
    })();
    // Runs once on mount; the code/state are captured from the entry URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthLayout>
      {status === "exchanging" && (
        <p className="auth__status" role="status">
          <span className="auth__spinner" aria-hidden="true" />
          {t("auth.signingIn")}
        </p>
      )}

      {status === "error" && (
        <>
          <p role="alert" className="banner banner--error">
            {t("auth.callbackError")}
          </p>
          <div className="auth__actions">
            <Link className="btn btn--primary btn--block" to="/login">
              {t("auth.backToLogin")}
            </Link>
          </div>
        </>
      )}
    </AuthLayout>
  );
}
